import SimulatorDataResource from "common/resources/ProductSheet/SimulatorDataResource";
import Button, { EButtonVariant } from "components/elements/Button";
import MessageBox from "components/elements/MessageBox";
import Typography, { ITypo, ITypoComponent } from "components/elements/Typography";
import InputElement, { EInputType } from "components/materials/Form/InputElement";
import classes from "./classes.module.scss";

export function simulatorData(value: unknown): SimulatorDataResource {
	return SimulatorDataResource.hydrate<SimulatorDataResource>(
		(value ?? { geography: [], sectors: [], reconstitutionValue: null, ongoingFees: null, transactionFees: null }) as SimulatorDataResource,
	);
}

export default function SimulatorInformation({ value, readonly, onChange }: { value: SimulatorDataResource; readonly: boolean; onChange: (data: SimulatorDataResource) => void }) {
	const update = (patch: Partial<SimulatorDataResource>) => onChange(simulatorData({ ...value, ...patch }));
	return (
		<section className={classes["root"]}>
			<Typography typo={ITypo.H2} component={ITypoComponent.H2}>
				Données du simulateur
			</Typography>
			{value.sourceDate && (
				<Typography typo={ITypo.P_SMALL}>
					Données complémentaires importées du simulateur · Source au {value.sourceDate}. Les prix et taux du catalogue Wenimmo restent la référence.
				</Typography>
			)}
			<div className={classes["scalars"]}>
				{(
					[
						["reconstitutionValue", "Valeur de reconstitution (€)", false],
						["ongoingFees", "Frais courants (% du montant investi par an)", true],
						["transactionFees", "Frais de transaction (% du montant investi par an)", true],
					] as const
				).map(([key, label, percent]) => (
					<InputElement
						key={key}
						name={`simulatorData.${key}`}
						label={label}
						type={EInputType.NUMBER}
						step={0.01}
						readonly={readonly}
						defaultValue={value[key] == null ? "" : Number((value[key]! * (percent ? 100 : 1)).toFixed(6))}
						placeholder="Non renseigné"
						onChange={(e) => update({ [key]: e.target.value === "" ? null : Number(e.target.value) / (percent ? 100 : 1) })}
					/>
				))}
			</div>
			<MessageBox
				type="info"
				text="Les frais courants et de transaction sont présentés selon la convention annuelle du montant investi. Ils ne sont pas déduits des revenus ni du TRI. Vérifiez les valeurs importées avec le document d'informations clés de la SCPI. Une répartition vide reste inconnue."
			/>
			<div className={classes["columns"]}>
				{(
					[
						["geography", "Répartition géographique"],
						["sectors", "Répartition sectorielle"],
					] as const
				).map(([key, title]) => {
					const entries = value[key];
					const total = entries.reduce((s, e) => s + e.weight, 0);
					return (
						<div className={classes["group"]} key={key}>
							<Typography typo={ITypo.P_LARGE_BOLD}>{title}</Typography>
							{entries.map((entry, index) => (
								<div className={classes["row"]} key={index}>
									<InputElement
										name={`simulatorData.${key}.${index}.label`}
										label={`${key === "geography" ? "Pays" : "Secteur"} ${index + 1}`}
										type={EInputType.TEXT}
										readonly={readonly}
										defaultValue={entry.label}
										onChange={(e) => update({ [key]: entries.map((row, i) => (i === index ? { ...row, label: e.target.value } : row)) })}
									/>
									<InputElement
										name={`simulatorData.${key}.${index}.weight`}
										label="Exposition (%)"
										type={EInputType.NUMBER}
										step={0.01}
										readonly={readonly}
										defaultValue={Number((entry.weight * 100).toFixed(6))}
										onChange={(e) =>
											update({
												[key]: entries.map((row, i) =>
													i === index ? { ...row, weight: e.target.value === "" ? NaN : Number(e.target.value) / 100 } : row,
												),
											})
										}
									/>
									{!readonly && (
										<Button variant={EButtonVariant.TEXT} onClick={() => update({ [key]: entries.filter((_, i) => i !== index) })}>
											Retirer
										</Button>
									)}
								</div>
							))}
							<Typography typo={ITypo.P_SMALL}>{entries.length ? `Total renseigné : ${(total * 100).toFixed(2)} %` : "Non renseignée"}</Typography>
							{(!Number.isFinite(total) || total > 1.000001) && <MessageBox type="warning" text="Le total des expositions doit être compris entre 0 et 100 %." />}
							{!readonly && (
								<Button variant={EButtonVariant.OUTLINED} onClick={() => update({ [key]: [...entries, { label: "", weight: 0 }] })}>
									Ajouter {key === "geography" ? "un pays" : "un secteur"}
								</Button>
							)}
						</div>
					);
				})}
			</div>
		</section>
	);
}
